import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useState } from "react"
import { api } from "../../../../convex/_generated/api"
import { parseNumberDraft } from "@/lib/numberDraft"

export const Route = createFileRoute("/_authed/admin/economy")({
  component: AdminEconomy,
})

function AdminEconomy() {
  const rows = useQuery(api.economy.snapshot)
  const setValue = useMutation(api.economy.setValue)
  const reset = useMutation(api.economy.resetToDefault)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)

  if (!rows) {
    return <p className="text-sm text-[color:var(--muted-foreground)]">Loading…</p>
  }

  async function handleSave(key: string) {
    const value = parseNumberDraft(edits[key] ?? "")
    if (value === null) return
    setSaving(key)
    try {
      await setValue({ key, value })
      setEdits({ ...edits, [key]: "" })
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Economy</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-foreground)]">
          Wild Points values, multipliers, streak rules, reward defaults.
          Changes take effect immediately and are audit-logged.
        </p>
      </header>

      <div className="rounded-md border border-[color:var(--border)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--muted)] text-xs uppercase tracking-wide text-[color:var(--muted-foreground)]">
            <tr>
              <th className="px-3 py-2 text-left">Key</th>
              <th className="px-3 py-2 text-right">Current</th>
              <th className="px-3 py-2 text-right">Default</th>
              <th className="px-3 py-2 text-right">New</th>
              <th className="px-3 py-2 w-32" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border)]">
            {rows.map((row) => {
              const draft = edits[row.key] ?? ""
              const draftValue = parseNumberDraft(draft)
              return (
                <tr key={row.key}>
                  <td className="px-3 py-2 font-mono text-xs">{row.key}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.value}
                    {row.isDefault && (
                      <span className="ml-1 font-mono text-[9px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
                        (default)
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-[color:var(--muted-foreground)]">
                    {row.defaultValue}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={draft}
                      onChange={(e) =>
                        setEdits({ ...edits, [row.key]: e.target.value })
                      }
                      aria-invalid={
                        draft !== "" && draftValue === null ? true : undefined
                      }
                      aria-label={`New value for ${row.key}`}
                      placeholder={String(row.value)}
                      className="w-24 rounded border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-right text-sm"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleSave(row.key)}
                      disabled={draftValue === null || saving === row.key}
                      className="rounded bg-[color:var(--primary)] px-2 py-1 text-[10px] font-medium text-[color:var(--primary-foreground)] disabled:opacity-50"
                    >
                      {saving === row.key ? "…" : "Save"}
                    </button>
                    {!row.isDefault && (
                      <button
                        onClick={() => reset({ key: row.key })}
                        className="ml-1 rounded border border-[color:var(--border)] px-2 py-1 text-[10px] hover:bg-[color:var(--muted)]"
                      >
                        Reset
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
